import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AiColTemplateDetail from './enrich-template-detail-step';
import { EnrichOwnPromptProps } from '../types';
import { RootState } from '../../../../../../../store/root-reducer';
import {
  resetGetTemplateById,
  resetGetTemplateConfigResponse,
  resetIsBulkProspectSelected,
  setAiColumnModalVisible,
  setSelectedAiTemplateShareableId,
  resetAiColumnTemplateRegenerate,
  resetgetTemplateByIdRequest,
  setAutoRunConditionFilterGroups,
  setAutoRunConditionFilterGroupsLogic,
  resetAutoRunConditionFilterGroups, 
  setIsAiColEnrichmentFromModal,
} from '../../../../../prospect-slice';
import {
  getAiModelRequest,
  getFieldsRequest,
  getTemplateByIdRequest,
  saveAndDontRun,
} from '../../../../../extra-actions';
import { getContactFieldsRequest } from '../../../../../../sequence/extra-actions';
import {
  getTemplateByIdParams,
  saveAndDontRunPayload,
} from '../../../../../types';
import { enableBulkActionRequestPolling } from '../../../../../../home/home-slice';
import { GetFieldsQueryParams } from '../../../../../../settings/types/request-payload';
import { getPostLoadMetaRequest } from '../../../../../../home/extra-actions';

const AiColTemplateDetailContainer = memo((props: EnrichOwnPromptProps) => {
  const dispatch = useDispatch();

  const fields = useSelector((state: RootState) => state?.prospect?.fields);
  const prospectFiltersFieldsList = useSelector((state: RootState) => state?.prospect?.prospectFiltersFieldsList);
  const selectedAiTemplateShareableId = useSelector(
    (state: RootState) => state?.prospect?.selectedAiTemplateShareableId,
  );
  const getTemplateByIdRequestStatus = useSelector(
    (state: RootState) => state?.prospect?.getTemplateByIdRequest?.status,
  );

  const getTemplateById = useSelector(
    (state: RootState) => state?.prospect?.getTemplateById,
  );
  const aiColumnTemplateRegenerateResponse = useSelector(
    (state: RootState) => state.prospect.aiColumnTemplateRegenerate,
  );
  const getTemplateConfigResponseStatus = useSelector(
    (state: RootState) => state?.prospect?.getTemplateConfigResponse?.status,
  );
  const saveAndDontRunStatus = useSelector(
    (state: RootState) => state?.prospect?.saveAndDontRun?.status,
  );
  const getAiModelList = useSelector(
    (state: RootState) => state?.prospect?.aiModelList,
  );
  // prospect list selected row id's
  const selectedProspectRowIds = useSelector(
    (state: RootState) => state?.prospect?.selectedProspectRowId,
  );
  const filter = useSelector((state: RootState) => state?.prospect?.filters);
  const deSelectedProspectRowId = useSelector(
    (state: RootState) => state?.prospect?.deSelectedProspectRowId,
  );
  const bulkProspectSelected = useSelector(
    (state: RootState) => state?.prospect?.isBulkProspectSelected,
  );
  const isProspectEnrichmentEnabledMeta = useSelector(
    (state: RootState) => state.home.isProspectEnrichmentEnabled,
  );
  const getVisibleFieldIds = useSelector(
    (state: RootState) => state?.prospect?.visibleFieldIds,
  );
  const aiCreditSingleCreditPrice = useSelector(
    (state: RootState) =>
      state?.billingAndSubscription?.defaultAiCreditRateResponse
        ?.currencyUnit || 0,
  );

  const autoRunConditionFilterGroups = useSelector(
    (state: RootState) => state?.prospect?.autoRunConditionFilterGroups,
  );
  const autoRunConditionFilterGroupsLogic = useSelector(
    (state: RootState) => state?.prospect?.autoRunConditionFilterGroupsLogic,
  );

  const setSelectedAiTemplateShareableIds = useCallback(
    (id: string) => {
      dispatch(setSelectedAiTemplateShareableId(id));
    },
    [dispatch],
  );

  const fetchAiModel = useCallback(() => {
    dispatch(getAiModelRequest());
  }, [dispatch]);

  const fetchContactFields = useCallback(() => {
    dispatch(getContactFieldsRequest());
  }, [dispatch]);

  const fetchTemplateById = useCallback(
    (payload: getTemplateByIdParams) => {
      dispatch(getTemplateByIdRequest(payload));
    },
    [dispatch],
  );

  const saveAndRun = useCallback(
    (payload: saveAndDontRunPayload) => {
      dispatch(saveAndDontRun(payload));
    },
    [dispatch],
  );

  const resetTemplateConfigResponse = useCallback(() => {
    dispatch(resetGetTemplateConfigResponse());
  }, [dispatch]);

  const resetAiColumnTemplateRegenerateResponse = useCallback(() => {
    dispatch(resetAiColumnTemplateRegenerate());
  }, [dispatch]);

  const enablePollingRequest = useCallback(() => {
    dispatch(enableBulkActionRequestPolling());
  }, [dispatch]);

  const fetchFields = useCallback(
    (payload: GetFieldsQueryParams) => {
      dispatch(getFieldsRequest(payload));
    },
    [dispatch],
  );

  const resetBulkProspectSelected = useCallback(() => {
    dispatch(resetIsBulkProspectSelected());
  }, [dispatch]);

  const resetTemplateById = useCallback(() => {
    dispatch(resetGetTemplateById());
  }, [dispatch]);

  const isAiColumnModalVisible = useCallback((payload: boolean) => {
    dispatch(setAiColumnModalVisible(payload));
  }, []);

  const resetGetTemplateByIdRequest = useCallback(() => {
    dispatch(resetgetTemplateByIdRequest());
  }, [dispatch]);

  const fetchPostLoadMetaRequest = useCallback(() => {
    dispatch(getPostLoadMetaRequest());
  }, [dispatch]);
  const setIsAiColEnrichmentFromModalAction = useCallback((payload: boolean) => {
    dispatch(setIsAiColEnrichmentFromModal(payload));
  }, [dispatch]);

  const handleSetAutoRunConditionFilterGroups = useCallback((payload: any) => {
    dispatch(setAutoRunConditionFilterGroups(payload));
  }, [dispatch]);

  const handleSetAutoRunConditionFilterGroupsLogic = useCallback((payload: any) => {
    dispatch(setAutoRunConditionFilterGroupsLogic(payload));
  }, [dispatch]);

  const handleResetAutoRunConditionFilterGroups = useCallback(() => {
    dispatch(resetAutoRunConditionFilterGroups());
  }, [dispatch]);

  return (
    <AiColTemplateDetail
      {...props}
      fields={fields}
      prospectFiltersFieldsList={prospectFiltersFieldsList}
      selectedAiTemplateShareableId={selectedAiTemplateShareableId}
      getTemplateByIdRequestStatus={getTemplateByIdRequestStatus}
      getTemplateById={getTemplateById}
      aiColumnTemplateRegenerateResponse={aiColumnTemplateRegenerateResponse}
      getTemplateConfigResponseStatus={getTemplateConfigResponseStatus}
      saveAndDontRunStatus={saveAndDontRunStatus}
      getAiModelList={getAiModelList}
      selectedProspectRowIds={selectedProspectRowIds}
      filter={filter}
      deSelectedProspectRowId={deSelectedProspectRowId}
      bulkProspectSelected={bulkProspectSelected}
      isProspectEnrichmentEnabledMeta={isProspectEnrichmentEnabledMeta}
      getVisibleFieldIds={getVisibleFieldIds}
      aiCreditSingleCreditPrice={Number(aiCreditSingleCreditPrice) || 0}
      setSelectedAiTemplateShareableIds={setSelectedAiTemplateShareableIds}
      fetchAiModel={fetchAiModel}
      fetchContactFields={fetchContactFields}
      fetchTemplateById={fetchTemplateById}
      saveAndRun={saveAndRun}
      resetTemplateConfigResponse={resetTemplateConfigResponse}
      enablePollingRequest={enablePollingRequest}
      fetchFields={fetchFields}
      resetBulkProspectSelected={resetBulkProspectSelected}
      resetTemplateById={resetTemplateById}
      isAiColumnModalVisible={isAiColumnModalVisible}
      resetAiColumnTemplateRegenerateResponse={
        resetAiColumnTemplateRegenerateResponse
      }
      resetGetTemplateByIdRequest={resetGetTemplateByIdRequest}
      fetchPostLoadMetaRequest={fetchPostLoadMetaRequest}
      setAutoRunConditionFilterGroups={handleSetAutoRunConditionFilterGroups}
      autoRunConditionFilterGroups={autoRunConditionFilterGroups}
      setAutoRunConditionFilterGroupsLogic={handleSetAutoRunConditionFilterGroupsLogic}
      autoRunConditionFilterGroupsLogic={autoRunConditionFilterGroupsLogic}
      resetAutoRunConditionFilterGroups={handleResetAutoRunConditionFilterGroups}
      setIsAiColEnrichmentFromModal={setIsAiColEnrichmentFromModalAction}
    />
  );
});

export default AiColTemplateDetailContainer;
